import type {
  ContentMixPreset,
  PlatformId,
  PrimaryGoal,
  StrategyPlatformCadence,
} from '@modules/content-plan/types';

export interface StrategyPreflightKeyInput {
  projectId: string;
  userId?: string;
  authorId?: string;
  name?: string;
  dateStart: string;
  dateEnd: string;
  targetPlatforms: PlatformId[];
  postsPerWeekPerPlatform: Partial<Record<PlatformId, number>>;
  platformCadences?: StrategyPlatformCadence[];
  primaryGoal: PrimaryGoal;
  contentMixPreset: ContentMixPreset;
  autoFillBacklog?: boolean;
  autoAssignTopicsToSlots?: boolean;
}

function sortedNumberRecord(
  value: Partial<Record<PlatformId, number>> | undefined,
): Array<[string, number]> {
  return Object.entries(value ?? {})
    .map(([key, count]) => [key, Number(count) || 0] as [string, number])
    .sort(([a], [b]) => a.localeCompare(b));
}

function normalizeCadences(
  cadences: StrategyPlatformCadence[] | undefined,
): Array<Record<string, unknown>> {
  return [...(cadences ?? [])]
    .sort((a, b) => String(a.platform).localeCompare(String(b.platform)))
    .map((cadence) => ({
      platform: cadence.platform,
      postsPerWeek: cadence.postsPerWeek,
      preferredWeekdays: [...(cadence.preferredWeekdays ?? [])].sort((a, b) => a - b),
      preferredTimesLocal: [...(cadence.preferredTimesLocal ?? [])].sort(),
      slots: [...(cadence.slots ?? [])]
        .sort((a, b) => {
          if (a.weekdayIndex !== b.weekdayIndex) return a.weekdayIndex - b.weekdayIndex;
          return a.timeLocal.localeCompare(b.timeLocal);
        })
        .map((slot) => ({
          weekdayIndex: slot.weekdayIndex,
          timeLocal: slot.timeLocal,
          score: slot.score,
        })),
    }));
}

export function buildStrategyPreflightKey(input: StrategyPreflightKeyInput): string {
  return JSON.stringify({
    projectId: input.projectId,
    userId: input.userId ?? '',
    authorId: input.authorId ?? '',
    name: input.name ?? '',
    dateStart: input.dateStart,
    dateEnd: input.dateEnd,
    targetPlatforms: [...input.targetPlatforms].sort(),
    postsPerWeekPerPlatform: sortedNumberRecord(input.postsPerWeekPerPlatform),
    platformCadences: normalizeCadences(input.platformCadences),
    primaryGoal: input.primaryGoal,
    contentMixPreset: input.contentMixPreset,
    autoFillBacklog: input.autoFillBacklog === true,
    autoAssignTopicsToSlots: input.autoAssignTopicsToSlots === true,
  });
}
